import type { ConsumerAddressFields } from "@jti/evo-client";
import {
  createEvoClient,
  deleteAddress,
  getProfile,
  setDefaultAddress,
  storeAddress,
  updateAddress,
} from "@jti/evo-client";
import type { Address, AddressStored } from "@jti/fe-common";

import {
  convertCasAddressToStoredAddress,
  convertStoredAddressToCasAddress,
  prepareAddresses,
  prepareAndFindAddress,
} from "../../utils";
import type { CasAddressesApiContract } from "../CasAddressesApi.types";

export class CasAddressesApiEvo implements CasAddressesApiContract {
  private client;
  constructor(
    baseUrl: string,
    protected tenantToken: string,
    protected ciamToken: string,
    protected recaptchaId: string,
    protected userUuid: string
  ) {
    this.client = createEvoClient({
      baseUrl,
      ciamToken,
      tenantToken,
      recaptchaId,
      throwOnError: true,
    });
  }

  async getAddresses(): Promise<{ addresses: AddressStored[] }> {
    const { data } = await getProfile({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
    });

    return { addresses: prepareAddresses(data?.data?.consumer?.addresses || []) };
  }

  async createAddress(address: Address): Promise<{
    addresses: AddressStored[];
    address: AddressStored;
  }> {
    const { data } = await storeAddress({
      client: this.client,
      path: {
        uuid: this.userUuid,
      },
      body: convertStoredAddressToCasAddress(address),
    });

    if (!data?.addresses || data.addresses.length === 0) {
      return Promise.reject(new Error("No addresses returned"));
    }

    const lastAddress = data.addresses[data.addresses.length - 1] as ConsumerAddressFields;

    return {
      addresses: prepareAddresses(data?.addresses || []),
      address: convertCasAddressToStoredAddress(lastAddress),
    };
  }

  async updateAddress(
    addressId: string,
    address: Address
  ): Promise<{
    addresses: AddressStored[];
    address?: AddressStored;
  }> {
    const { data } = await updateAddress({
      client: this.client,
      path: {
        addressId: addressId as unknown as number,
        uuid: this.userUuid,
      },
      body: convertStoredAddressToCasAddress(address),
    });

    if (!data?.addresses) {
      return Promise.reject(new Error("No addresses returned"));
    }

    const { storedAddresses, updatedAddress } = prepareAndFindAddress(data.addresses, addressId);

    return {
      addresses: storedAddresses,
      address: updatedAddress,
    };
  }

  async setDefaultAddress(
    addressId: string,
    addressType: "billing" | "shipping"
  ): Promise<{
    addresses: AddressStored[];
    address?: AddressStored;
  }> {
    const { data } = await setDefaultAddress({
      client: this.client,
      path: {
        addressId: addressId as unknown as number,
        uuid: this.userUuid,
        addressType,
      },
    });

    if (!data?.addresses) {
      return Promise.reject(new Error("No addresses returned"));
    }

    const { storedAddresses, updatedAddress } = prepareAndFindAddress(data.addresses, addressId);

    return {
      addresses: storedAddresses,
      address: updatedAddress,
    };
  }

  async setDefaultShippingAddress(addressId: string): Promise<{
    addresses: AddressStored[];
    address?: AddressStored;
  }> {
    return await this.setDefaultAddress(addressId, "shipping");
  }

  async setDefaultBillingAddress(addressId: string): Promise<{
    addresses: AddressStored[];
    address?: AddressStored;
  }> {
    return await this.setDefaultAddress(addressId, "billing");
  }

  async deleteAddress(addressId: string): Promise<void> {
    await deleteAddress({
      client: this.client,
      path: {
        addressId: addressId as unknown as number,
        uuid: this.userUuid,
      },
    });
  }
}
